import { ClipboardList } from "lucide-react";

import {
  copyValuesFor,
  listAnsweredScreens,
  QUESTIONNAIRE_COPY,
  readPassengerAnswers,
  requiresAdminReview,
  screenTitle,
  summarizeAnswer,
} from "@upclaim/lib/src/passenger-answers";

import { Badge } from "@/components/ui/badge";

/**
 * The passenger's answers to the CONFIRM questionnaire (TECH-1097), question by question — what
 * the passenger saw, in French whatever language they answered in. The free note is not a row
 * here: it shows apart, as the « Note passager » (`passenger_note`).
 *
 * @returns `null` when the acquisition was confirmed with the free note.
 */
export function PassengerAnswersPanel({ value }: { value: unknown }) {
  const answers = readPassengerAnswers(value);

  if (!answers) {
    return null;
  }

  const copy = QUESTIONNAIRE_COPY.fr;
  const values = copyValuesFor(copy, null, "la compagnie");
  const review = requiresAdminReview(answers);

  return (
    <section className="mt-2" aria-label="Réponses au questionnaire">
      <div className="flex flex-wrap items-center gap-2">
        <p className="inline-flex items-center text-base font-semibold text-gray-900">
          <ClipboardList className="mr-2 h-5 w-5 text-gray-500" />
          Réponses au questionnaire
        </p>
        {review ? <Badge variant="warning">Revue demandée, pas de signature</Badge> : null}
        {answers.otherIssues?.length ? <Badge variant="info">Hors 261 : traité à part</Badge> : null}
        {answers.locale !== "fr" ? <Badge variant="outline">Répondu en anglais</Badge> : null}
      </div>
      <dl className="mt-3 divide-y rounded-lg border bg-white text-sm">
        {listAnsweredScreens(answers)
          .filter((screen) => screen !== "PRECISIONS" || Boolean(answers.precisions?.question))
          .map((screen) => (
            <div key={screen} className="grid gap-1 px-3 py-2 sm:grid-cols-5 sm:gap-4">
              <dt className="text-gray-500 sm:col-span-2">
                {screen === "PRECISIONS" && answers.precisions?.question
                  ? answers.precisions.question
                  : screenTitle(copy, screen, values)}
              </dt>
              <dd className="font-medium whitespace-pre-line text-gray-900 sm:col-span-3">
                {summarizeAnswer(copy, screen, answers, "fr")}
              </dd>
            </div>
          ))}
        {answers.verification.status === "NOT_TRAVELLED" && answers.verification.details ? (
          // The correction screen shows its comment with its answer; this one is typed on the recap.
          <div className="grid gap-1 px-3 py-2 sm:grid-cols-5 sm:gap-4">
            <dt className="text-gray-500 sm:col-span-2">Commentaire du passager</dt>
            <dd className="font-medium whitespace-pre-line text-gray-900 sm:col-span-3">
              « {answers.verification.details} »
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}
